import React, { useState, useEffect } from "react";
import Navbar from "../signin/Navbar.tsx";
import LeftPanel from "../signin/LeftPanel.tsx";
import { InputAdornment, IconButton, TextField, Button } from "@mui/material";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../redux/store";
import { resetPassword } from "../../redux/features/forgot-reset-Password/resetPasswordSlice.tsx";
import { toast } from "react-toastify";

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [touchedFields, setTouchedFields] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const {
    loading,
    success,
    error: apiError,
  } = useSelector((state: RootState) => state.resetPassword);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing token.");
    }
  }, [token]);

  useEffect(() => {
    if (success) {
      navigate("/signin");
    }
  }, [success, navigate]);

  // Real-time validation
  useEffect(() => {
    if (newPassword || touchedFields.newPassword) {
      validatePassword();
    }
    if (confirmPassword || touchedFields.confirmPassword) {
      validateConfirmPassword();
    }
  }, [newPassword, confirmPassword]);

  const validatePassword = () => {
    const errors: string[] = [];

    if (!newPassword) {
      errors.push("Password is required");
    } else {
      if (newPassword.length < 8) {
        errors.push("Must be at least 8 characters");
      }
      if (newPassword.length > 16) {
        errors.push("Must not exceed 16 characters");
      }
      if (!/(?=.*[a-z])/.test(newPassword)) {
        errors.push("Must contain a lowercase letter");
      }
      if (!/(?=.*[A-Z])/.test(newPassword)) {
        errors.push("Must contain an uppercase letter");
      }
      if (!/(?=.*\d)/.test(newPassword)) {
        errors.push("Must contain a number");
      }
      if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(newPassword)) {
        errors.push("Must contain a special character");
      }
    }

    setPasswordErrors(errors);
  };

  const validateConfirmPassword = () => {
    if (!confirmPassword) {
      setConfirmPasswordError("Confirm password is required");
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  };

  const isFormValid = () => {
    return (
      passwordErrors.length === 0 &&
      confirmPasswordError === "" &&
      newPassword === confirmPassword &&
      newPassword.length > 0 &&
      confirmPassword.length > 0
    );
  };

  const handleResetPassword = async () => {
    if (token && isFormValid()) {
      try {
        await dispatch(
          resetPassword({ token, newPassword, confirmPassword })
        ).unwrap();
        toast.success(`Password reset successfully`);
      } catch (err) {
        toast.error(err);
      }
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <Navbar />
      <div className="flex flex-1">
        <div className="w-1/2 hidden md:block">
          <LeftPanel />
        </div>
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8 px-3 sm:px-8 lg:px-32">
          <>
            <h2 className="mb-14 text-[44px] text-[#3D3A3A] font-bold text-center">
              Reset Your Password
            </h2>
            <form className="flex flex-col gap-4">
              <TextField
                variant="outlined"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={() =>
                  setTouchedFields({ ...touchedFields, newPassword: true })
                }
                placeholder="Enter New Password"
                error={touchedFields.newPassword && passwordErrors.length > 0}
                helperText={
                  touchedFields.newPassword && passwordErrors.length > 0
                    ? passwordErrors.join(", ")
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock style={{ color: "#B0B0B0" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? (
                          <Visibility sx={{ color: "#3BC1CD" }} />
                        ) : (
                          <VisibilityOff sx={{ color: "#3BC1CD" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: {
                    border: "1px solid #C4C4C4",
                    borderRadius: "10px",
                    padding: "0px 14px",
                    background: "#fff",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  input: {
                    color: "#333333",
                  },
                }}
              />
              <TextField
                variant="outlined"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() =>
                  setTouchedFields({ ...touchedFields, confirmPassword: true })
                }
                placeholder="Confirm New Password"
                error={
                  touchedFields.confirmPassword && confirmPasswordError !== ""
                }
                helperText={
                  touchedFields.confirmPassword ? confirmPasswordError : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock style={{ color: "#B0B0B0" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <Visibility sx={{ color: "#3BC1CD" }} />
                        ) : (
                          <VisibilityOff sx={{ color: "#3BC1CD" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: {
                    border: "1px solid #C4C4C4",
                    borderRadius: "10px",
                    padding: "0px 14px",
                    background: "#fff",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  input: {
                    color: "#333333",
                  },
                }}
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              {apiError && <p className="text-red-500 text-sm">{apiError}</p>}
              <Button
                variant="contained"
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "#3BC1CD",
                  fontSize: "16px",
                  padding: "12px 0px",
                  marginTop: "36px",
                  "&.Mui-disabled": {
                    backgroundColor: "#B0B0B0",
                    color: "#FFFFFF",
                  },
                }}
                fullWidth
                onClick={handleResetPassword}
                disabled={loading || !isFormValid()}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
